/**
 * The single (+) at the top of the Wish tab, and where it can go.
 *
 * Chase said three choices and named four: adding to a binder that exists and
 * making a new one land in different places, so both are here.
 */

import { CreditCard, Layers, FolderPlus, FolderOpen } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';

export const WISH_ADD = {
  CARD: 'card',
  MASTER: 'master',
  TO_BINDER: 'to-binder',
  NEW_BINDER: 'new-binder',
};

const OPTIONS = [
  { id: WISH_ADD.CARD, label: 'Add a Chase Card', hint: 'Search and pick, straight into your ranked ten', Icon: CreditCard },
  // Says that looking is free, because it is: browsing sets and their cards
  // costs nothing and adds nothing until it is asked for.
  { id: WISH_ADD.MASTER, label: 'Add a Master or Sub Set', hint: 'Open any set and look before you commit to it', Icon: Layers },
  { id: WISH_ADD.TO_BINDER, label: 'Add to a Chase Binder', hint: 'Pick the binder, then pick cards', Icon: FolderOpen },
  { id: WISH_ADD.NEW_BINDER, label: 'Create a Chase Binder', hint: 'Name it first, then fill it', Icon: FolderPlus },
];

export default function WishAddSheet({ isOpen, onClose, onPick }) {
  const { theme } = useTheme();

  return (
    <BottomSheet
      open={!!isOpen}
      onClose={onClose}
      label="Add to your chase list"
      overlayStyle={{ ...styles.overlay, backgroundColor: theme.overlay }}
      sheetStyle={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
    >
      <div style={styles.handle} />
      <h2 style={{ ...styles.title, color: theme.text }}>What are you chasing?</h2>
      {OPTIONS.map(({ id, label, hint, Icon }) => (
        <button
          key={id}
          style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
          onClick={() => { haptics.tap(); onPick?.(id); }}
        >
          <Icon size={20} color={theme.accent} />
          <span style={styles.rowText}>
            <span style={{ ...styles.rowLabel, color: theme.text }}>{label}</span>
            <span style={{ ...styles.rowHint, color: theme.textMuted }}>{hint}</span>
          </span>
        </button>
      ))}
    </BottomSheet>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 12px' },
  title: { fontSize: '17px', fontWeight: '600', margin: '0 0 6px' },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '58px', padding: '0 14px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', textAlign: 'left',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowText: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  rowLabel: { fontSize: '15px', fontWeight: '600' },
  rowHint: { fontSize: '12px' },
};
